import type { Patient, Reference } from '@medplum/fhirtypes'
import type { DateString } from '@seen/core/dates'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { ExtendedObservation } from '@seen/core/models/observation'
import { Button, TypographySubtle } from '@seen/ui'
import {
  IconCopy,
  IconLock,
  IconLockOpen,
  IconRestore,
  IconTemplate,
} from '@tabler/icons-react'
import { skipToken } from '@tanstack/react-query'
import { type JSX, useEffect, useRef, useState } from 'react'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { TemplateCombobox } from '#src/components/TemplateCombobox.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useQuery } from '#src/hooks/useQuery.ts'
import { useResetDayCenterDayNote } from '#src/hooks/useResetDayCenterDayNote.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import {
  DAY_CENTER_DAY_NOTE_CODE,
  useStartDayCenterDayNote,
} from '#src/hooks/useStartDayCenterDayNote.ts'
import { contentToHTML } from '#src/utils/noteContent.ts'

/* The body the retired render-time auto-create seeded every day note with.
 * Nothing writes it any more; it survives only as a reader, because notes
 * already in the chart carry exactly this HTML and have to keep opening
 * unlocked. Delete it once those notes are retracted. */
const LEGACY_AUTO_CREATED_BODY =
  '<p>Feeding:</p><p></p><p>Transfers:</p><p></p><p>Toileting:</p><p></p><p>Mobility:</p><p></p><p>Vitals:</p><p></p><p>Cognitive:</p><p></p><p>Notes:</p><p></p><p></p><p></p>'

interface DayNoteProps {
  participantReference: Reference<Patient> | undefined
  selectedDate: DateString
}

/* Nothing is written until the clinician picks a starting point. Both buttons
 * render the same for every participant — deciding between them from history
 * would cost a lookback request per row on every render, which is the cost this
 * design exists to avoid. The lookback happens on click instead. */
export function DayCenterDayNote({
  participantReference,
  selectedDate,
}: DayNoteProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const { data: existingNote, isPending: isLoadingNote } = useSearchOne(
    ExtendedObservation,
    participantReference
      ? ExtendedObservation.searchQuery({
          subject: participantReference,
          code: DAY_CENTER_DAY_NOTE_CODE,
          date: selectedDate,
        })
      : undefined
  )

  /* A note this row already started, surviving both the row unmounting and the
   * day search refetching to a miss. Pure cache read — `skipToken` means it
   * never fetches; `useStartDayCenterDayNote` is the only writer. */
  const { data: startedNote } = useQuery<ExtendedObservation | null>({
    queryKey: cacheKeys.dayCenterDayNoteStarted(
      participantReference?.reference,
      selectedDate
    ),
    queryFn: skipToken,
    staleTime: Number.POSITIVE_INFINITY,
  })

  const start = useStartDayCenterDayNote({ participantReference, selectedDate })
  useErrorToast(
    !!start.error && 'Could not start this Day Center note. Please try again.'
  )

  const reset = useResetDayCenterDayNote({ participantReference, selectedDate })
  useErrorToast(!!reset.error && 'Could not start this Day Center note over.')
  const openConfirmation = useDeleteConfirmationStore((state) => state.open)

  /* The live search wins when it has the note. `start.data` covers the frames
   * right after the write, while the search is still refetching, and
   * `startedNote` covers everything after a remount discards `start.data`. */
  const note = existingNote ?? start.data?.note ?? startedNote
  const startedHere = !!start.data

  const onStartOver =
    note && !isReadOnly && note.isPreliminary
      ? () =>
          openConfirmation({
            title: 'Start this note over?',
            description:
              "Everything written in this day's note is discarded, and you can start again from the previous note or a template. The discarded note can still be recovered by contacting our team.",
            deleteText: 'Discard and start over',
            onConfirm: () => {
              reset.mutate(note, { onSuccess: () => start.reset() })
            },
          })
      : undefined

  if (note) {
    return (
      <div className="flex flex-col gap-1">
        {start.data?.foundPrevious === false && (
          <TypographySubtle className="text-xs text-foreground-secondary">
            No previous note for this participant — started from a blank one.
            Try Start from template instead.
          </TypographySubtle>
        )}
        <DayNoteEditor
          note={note}
          startUnlocked={startedHere}
          onStartOver={onStartOver}
        />
      </div>
    )
  }

  /* A skipped query (no participant) stays `pending` forever, so it must not
   * hold the row on the skeleton. */
  if (isLoadingNote && participantReference) {
    return <DayNoteSkeleton />
  }

  if (isReadOnly) {
    return (
      <TypographySubtle className="text-sm text-foreground-secondary">
        No note for this day.
      </TypographySubtle>
    )
  }

  const pendingSource = start.isPending ? start.variables?.source : undefined

  return (
    <div className="flex items-center gap-2 print:hidden">
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 bg-background"
        loading={pendingSource === 'previous'}
        disabled={start.isPending}
        onClick={() => start.mutate({ source: 'previous' })}
      >
        <IconCopy className="size-3.5" />
        Start from previous
      </Button>
      <TemplateCombobox
        templateClass={NoteTemplate}
        searchPlaceholder="Search templates..."
        onSelect={(template) =>
          start.mutate({
            source: 'template',
            html: contentToHTML(template.data),
          })
        }
      >
        <Button
          variant="ghost"
          size="xs"
          className="gap-1 text-foreground-secondary"
          loading={pendingSource === 'template'}
          disabled={start.isPending}
        >
          <IconTemplate className="size-3" />
          Start from template
        </Button>
      </TemplateCombobox>
    </div>
  )
}

function DayNoteSkeleton(): JSX.Element {
  return <div className="h-9 animate-pulse rounded-md bg-muted" />
}

function DayNoteEditor({
  note,
  startUnlocked = false,
  onStartOver,
}: {
  note: ExtendedObservation | null | undefined
  startUnlocked?: boolean
  onStartOver?: () => void
}): JSX.Element {
  const [unlocked, setUnlocked] = useState(startUnlocked)

  // Start unlocked if the note has nothing charted in it yet.
  // Only apply once on initial load so refetches don't override user's lock.
  const hasAppliedInitialUnlock = useRef(startUnlocked)
  useEffect(() => {
    if (!hasAppliedInitialUnlock.current && note) {
      hasAppliedInitialUnlock.current = true
      if (!note.valueString || note.valueString === LEGACY_AUTO_CREATED_BODY) {
        setUnlocked(true)
      }
    }
  }, [note])

  return (
    <div className="relative">
      <ResourceFieldForm
        instance={note}
        valueFn={(instance) => instance.valueString}
        patchFn={(instance, value) => {
          // `||`, not `??`: a cleared editor yields "", which FHIR rejects.
          instance.valueString = value || undefined
        }}
        debounceTyping
        disabled={!unlocked}
        render={({ field }) => (
          <RichTextEditor variant="p" className="text-sm" {...field} />
        )}
      />
      <div className="absolute top-1 right-1 flex items-center gap-1 print:hidden">
        {onStartOver && (
          <MoreDropdownMenu size="icon-sm" className="my-0 size-7 py-0">
            <DropdownMenuItem onSelect={onStartOver}>
              <IconRestore className="size-4 text-foreground-secondary" />
              Start over…
            </DropdownMenuItem>
          </MoreDropdownMenu>
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 bg-background"
          onClick={() => setUnlocked((prev) => !prev)}
        >
          {unlocked ? (
            <>
              <IconLockOpen className="size-3.5" />
              Unlocked
            </>
          ) : (
            <>
              <IconLock className="size-3.5" />
              Locked
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
